// Routes a PackerChartSeriesPayload to the matching renderer based on
// chart_type. Header (title + subtitle) is rendered here so it's
// consistent across every template.
//
// ⚠️ THE TWO CHART BODIES ARE LAZY, AND THAT IS LOAD-BEARING FOR THE WHOLE
//    SITE — NOT A MICRO-OPTIMIZATION FOR THIS COMPONENT.
//
//    recharts is 396 KB gzipped, and until 2026-09-28 it was in the EAGER
//    entry bundle of every page in the product, including pages with no chart
//    on them. The chain was entirely static:
//
//      App.tsx → Layout → ConsultantDrawer → ConsultantChat
//                       → MessagePartRenderer → ChartDispatcher
//                       → PackerChartLine / PackerChartBar → recharts
//
//    Layout wraps every route, so the consultant drawer — and therefore
//    recharts — loaded before a visitor could read anything. It was found
//    chasing a slow first paint on /sheet, a page that draws no charts at all.
//
//    Only these two files import recharts anywhere in the eager graph, so
//    these two lazy() calls are the entire fix. Keep them dynamic: a static
//    `import { PackerChartLine }` here silently puts 396 KB back on the
//    critical path of every page, and nothing in CI would fail.
//
// ⚠️ IF YOU RENDER THIS OFF-SCREEN TO RASTERIZE IT, AWAIT preloadChartBodies()
//    FIRST. See the note on that function — a rasterizer that doesn't will
//    capture the loading spinner and will NOT throw.

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, AlertCircle } from "lucide-react";
import { lazy, Suspense } from "react";
import type { PackerChartSeriesPayload } from "@/hooks/usePackerChartSeries";

// Named separately from the lazy() calls so preloadChartBodies() can await the
// SAME module requests the renderers use. Repeat import() of one specifier is
// deduped by the module registry, so this is one network fetch, not two.
const lineModule = () => import("./PackerChartLine");
const barModule = () => import("./PackerChartBar");

const PackerChartLine = lazy(() =>
  lineModule().then((m) => ({ default: m.PackerChartLine })),
);
const PackerChartBar = lazy(() =>
  barModule().then((m) => ({ default: m.PackerChartBar })),
);

/** Fetch the chart-body chunks up front.
 *
 *  For anything that renders ChartDispatcher into a detached container and
 *  screenshots it — `chartToPng` for Word export is the one caller today.
 *  Those rasterizers wait a couple of animation frames for recharts to settle,
 *  which is ~32 ms: plenty for a mount, nowhere near enough to pull 396 KB
 *  over the network. Without this the capture silently lands on the Suspense
 *  fallback and the exported document gets a PNG of a spinner — no exception,
 *  no failed test, just a wrong file in a customer's hands.
 *
 *  Awaiting this leaves React one microtask of suspend on first render, which
 *  resolves long before the next animation frame. */
export function preloadChartBodies(): Promise<unknown> {
  return Promise.all([lineModule(), barModule()]);
}

// Matches the h-[320px] that both chart bodies use for their own empty state,
// so the card doesn't resize when the real chart arrives.
function ChartBodyFallback() {
  return (
    // data-rb-chart-loading is how chartDispatcherLazy.test.tsx tells "the
    // chunk has not arrived yet" apart from "the chart loaded and had no rows".
    // Both states are a 320px box with centred grey content, so without a
    // marker a test cannot prove the lazy import ever resolved.
    <div
      data-rb-chart-loading="true"
      className="flex items-center justify-center h-[320px]"
    >
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
    </div>
  );
}

// Map the chart template to its upstream data source so the footer
// disclosure stays honest. Feeder trend templates (render_feeder_trend_chart)
// and steer/heifer spread pull from DVAuction; everything else is boxed-beef.
function dataSourceForTemplate(templateId: string): string {
  // The chart builder (2026-10-10) draws whatever series were asked for, so no
  // single source fits; each series' own citation is in the provenance row
  // under the chart. ⚠️ Without this case it fell to the boxed-beef default
  // below and an RB index chart was labeled "USDA LM_XB403 boxed beef."
  if (templateId === "chart_builder") {
    return "Each series' source is listed below the chart.";
  }
  if (templateId === "steer_heifer_spread") {
    return "DVAuction sale barn data (steers + heifers).";
  }
  // USDA-only head-to-head feeder comparison (get_usda_feeder_comparison).
  // Must be matched BEFORE any feeder_ heuristic and never labeled DVAuction —
  // this tool reads the USDA AMS auction feed exclusively (DV-independence hedge).
  if (templateId === "usda_feeder_comparison") {
    return "USDA AMS auction barn data (official weekly).";
  }
  if (templateId.startsWith("feeder_")) {
    return "DVAuction sale barn data.";
  }
  // Session 78 consultant chart tools for the new domains.
  if (templateId === "drought_national_dsci") {
    return "U.S. Drought Monitor (USDA + NOAA + NDMC).";
  }
  if (templateId === "cold_storage_monthly") {
    return "USDA NASS Monthly Cold Storage.";
  }
  if (templateId === "cot_managed_money") {
    return "CFTC Commitments of Traders (Disaggregated).";
  }
  if (templateId === "captive_supply_history") {
    return "USDA LMR LM_CT142 captive supply.";
  }
  if (templateId === "cow_calf_cost_30yr") {
    return "USDA ERS Cow-Calf Costs and Returns.";
  }
  // Session 80 parity-round-2 chart companions.
  if (templateId === "fed_cash_weekly") {
    return "USDA LMR LM_CT154 national weekly cash.";
  }
  if (templateId === "cutter_cow_cutout") {
    return "USDA LMR LM_XB405 cutter cow cutout.";
  }
  if (templateId === "carcass_weight_weekly") {
    return "USDA NASS Weekly Livestock Slaughter.";
  }
  if (templateId === "slaughter_pace_daily") {
    return "USDA MARS Daily Livestock Slaughter (estimated).";
  }
  if (templateId === "drop_value_daily") {
    return "USDA AMS NW_LS441/447 by-product drop value.";
  }
  if (templateId === "feeding_extension_monthly") {
    return "USDA LMR LSWFEDCC cash-trade weight + NASS Monthly Livestock Slaughter.";
  }
  if (templateId === "cof_history") {
    return "USDA NASS Cattle on Feed (1,000+ head feedlots).";
  }
  if (templateId === "packer_margin_history") {
    return "USDA LM_XB403 cutout + LM_CT100 5-area cash + NW_LS441/447 drop value.";
  }
  if (templateId === "cattle_inventory_history") {
    return "USDA NASS Cattle Inventory (Jan 1).";
  }
  if (templateId === "nass_cattle_prices") {
    return "USDA NASS Agricultural Prices (monthly).";
  }
  // All render_boxed_beef_chart templates use the LM_XB403 report.
  return "USDA LM_XB403 boxed beef.";
}

// Feeder templates already include "{N} trading days" in their subtitle string,
// so the generic "· N data points" tag-on would duplicate information.
function shouldAppendSampleCount(templateId: string): boolean {
  return !templateId.startsWith("feeder_");
}

interface Props {
  payload: PackerChartSeriesPayload | undefined;
  isLoading: boolean;
  error: Error | null;
}

export function ChartDispatcher({ payload, isLoading, error }: Props) {
  if (isLoading) {
    return (
      <Card className="border-border">
        <CardContent className="flex items-center justify-center h-[360px]">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-border">
        <CardContent className="flex items-center justify-center h-[360px] text-sm text-destructive gap-2">
          <AlertCircle className="h-4 w-4" />
          Couldn't load chart: {error.message}
        </CardContent>
      </Card>
    );
  }

  if (!payload) return null;

  return (
    <Card className="border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold">
          {payload.title}
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-0.5">
          {payload.subtitle}
          {shouldAppendSampleCount(payload.template_id) &&
            payload.meta.sample_count > 0 && (
              <>
                {" · "}
                {payload.meta.sample_count} data points
              </>
            )}
        </p>
      </CardHeader>
      <CardContent>
        <Suspense fallback={<ChartBodyFallback />}>
          {payload.chart_type === "line" && (
            <PackerChartLine payload={payload} />
          )}
          {payload.chart_type === "bar" && <PackerChartBar payload={payload} />}
        </Suspense>
        {payload.chart_type === "none" && (
          <p className="text-sm text-muted-foreground text-center py-20">
            Unknown chart template.
          </p>
        )}
        <p className="text-[11px] text-muted-foreground italic leading-relaxed mt-3 pt-3 border-t border-border">
          {dataSourceForTemplate(payload.template_id)} Weekend and holiday gaps
          are omitted. Data from {payload.meta.actual_start_date ?? "n/a"} to{" "}
          {payload.meta.actual_end_date ?? "n/a"}.
        </p>
      </CardContent>
    </Card>
  );
}
